import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon, FileSpreadsheetIcon, CloudIcon, CheckCircle2Icon, Loader2Icon, ClockIcon, Trash2Icon, DownloadIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useApp, ExportJob } from '../../context/AppContext';
function statusBadge(job: ExportJob) {
  switch (job.status) {
    case 'queued':
      return <span className="inline-flex items-center gap-1 rounded-full bg-navy-50 px-2 py-0.5 text-[11px] font-medium text-navy-600" data-id="element-49">
          <ClockIcon className="h-3 w-3" data-id="element-50" /> Queued
        </span>;
    case 'processing':
      return <span className="inline-flex items-center gap-1 rounded-full bg-gold-50 px-2 py-0.5 text-[11px] font-medium text-gold-700" data-id="element-51">
          <Loader2Icon className="h-3 w-3 animate-spin" data-id="element-52" /> Processing
        </span>;
    case 'ready':
      return <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700" data-id="element-53">
          <CheckCircle2Icon className="h-3 w-3" data-id="element-54" /> Ready
        </span>;
    case 'synced':
      return <span className="inline-flex items-center gap-1 rounded-full bg-accent-50 px-2 py-0.5 text-[11px] font-medium text-accent-800" data-id="element-55">
          <CloudIcon className="h-3 w-3" data-id="element-56" /> In Drive
        </span>;
  }
}
export function ExportQueuePanel() {
  const {
    exports,
    clearExport,
    queuePanelOpen,
    setQueuePanelOpen,
    driveSynced,
    setDriveSynced
  } = useApp();
  return <AnimatePresence data-id="element-57">
      {queuePanelOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.15
      }} className="fixed inset-0 z-40 bg-navy-950/30 backdrop-blur-[2px]" onClick={() => setQueuePanelOpen(false)} aria-hidden="true" data-id="element-58" />
          <motion.aside initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        type: 'spring',
        stiffness: 380,
        damping: 36
      }} className="card-float-lg fixed inset-y-3 right-3 z-50 flex w-full max-w-md flex-col overflow-hidden rounded-2xl bg-white" role="dialog" aria-label="Export queue" data-id="element-59">
            <div className="flex h-16 items-center justify-between border-b border-navy-50 px-5" data-id="element-60">
              <div data-id="element-61">
                <h2 className="text-sm font-semibold text-navy-900" data-id="element-62">
                  Export Queue
                </h2>
                <p className="text-xs text-navy-400" data-id="element-63">
                  Exports are prepared in the background, then delivered.
                </p>
              </div>
              <button type="button" onClick={() => setQueuePanelOpen(false)} aria-label="Close export queue" className="flex h-8 w-8 items-center justify-center rounded-full text-navy-400 transition-colors hover:bg-navy-50 hover:text-navy-900" data-id="element-64">
                <XIcon className="h-4 w-4" data-id="element-65" />
              </button>
            </div>

            <div className="border-b border-navy-50 bg-navy-50/50 px-5 py-4" data-id="element-66">
              <div className="flex items-center justify-between gap-3" data-id="element-67">
                <div className="flex items-center gap-3" data-id="element-68">
                  <div className={`flex h-9 w-9 items-center justify-center rounded-lg transition-colors ${driveSynced ? 'bg-accent-700 text-white' : 'bg-white text-navy-300 card-float'}`} data-id="element-69">
                    <CloudIcon className="h-4 w-4" data-id="element-70" />
                  </div>
                  <div data-id="element-71">
                    <p className="text-sm font-medium text-navy-900" data-id="element-72">
                      Google Drive sync
                    </p>
                    <p className="text-xs text-navy-400" data-id="element-73">
                      {driveSynced ? 'Completed exports save to SureLink Reports/' : 'Not connected — exports queue for download'}
                    </p>
                  </div>
                </div>
                <button type="button" role="switch" aria-checked={driveSynced} aria-label="Toggle Google Drive sync" onClick={() => {
              setDriveSynced(!driveSynced);
              toast(driveSynced ? 'Google Drive disconnected' : 'Google Drive connected');
            }} className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${driveSynced ? 'bg-accent-700' : 'bg-navy-200'}`} data-id="element-74">
                  <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${driveSynced ? 'left-[22px]' : 'left-0.5'}`} data-id="element-75" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto thin-scroll" data-id="element-76">
              {exports.length === 0 ? <div className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center" data-id="element-77">
                  <FileSpreadsheetIcon className="h-8 w-8 text-navy-200" data-id="element-78" />
                  <p className="text-sm font-medium text-navy-900" data-id="element-79">
                    No exports yet
                  </p>
                  <p className="text-xs text-navy-400" data-id="element-80">
                    Run a report and choose Export to queue a spreadsheet.
                  </p>
                </div> : <ul className="divide-y divide-navy-50" data-id="element-81">
                  <AnimatePresence initial={false} data-id="element-82">
                    {exports.map(job => <motion.li key={job.id} layout initial={{
                opacity: 0,
                y: -8
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                height: 0
              }} className="flex items-start gap-3 px-5 py-4" data-id="element-83">
                        <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600" data-id="element-84">
                          <FileSpreadsheetIcon className="h-4 w-4" data-id="element-85" />
                        </div>
                        <div className="min-w-0 flex-1" data-id="element-86">
                          <p className="truncate text-sm font-medium text-navy-900" data-id="element-87">
                            {job.reportName}
                          </p>
                          <p className="mt-0.5 text-xs text-navy-400" data-id="element-88">
                            {job.rowCount} rows · {job.format.toUpperCase()} ·{' '}
                            {job.requestedAt}
                          </p>
                          <div className="mt-1.5" data-id="element-89">{statusBadge(job)}</div>
                        </div>
                        <div className="flex shrink-0 items-center gap-1" data-id="element-90">
                          {job.status === 'ready' && <button type="button" onClick={() => toast.success(`Downloading ${job.reportName}.${job.format}`)} aria-label={`Download ${job.reportName}`} className="flex h-8 w-8 items-center justify-center rounded-full text-accent-700 transition-colors hover:bg-accent-50" data-id="element-91">
                              <DownloadIcon className="h-4 w-4" data-id="element-92" />
                            </button>}
                          <button type="button" onClick={() => clearExport(job.id)} aria-label={`Remove ${job.reportName} from queue`} className="flex h-8 w-8 items-center justify-center rounded-full text-navy-300 transition-colors hover:bg-navy-50 hover:text-rose-600" data-id="element-93">
                            <Trash2Icon className="h-4 w-4" data-id="element-94" />
                          </button>
                        </div>
                      </motion.li>)}
                  </AnimatePresence>
                </ul>}
            </div>
          </motion.aside>
        </>}
    </AnimatePresence>;
}